import type { DataPointSnapResult, SnapStrategyContext } from '../types';

import { BaseSnapStrategy } from './BaseSnapStrategy';

/** Continuous scales have no bandwidth (unlike band scales) */
const NO_BANDWIDTH = 0;

/**
 * Snap strategy for continuous scales (time, linear).
 *
 * Uses point-based snapping:
 * - Snaps to data points that are within snapDistance of the cursor
 * - Crosshair visibility is configurable
 */
export class PointSnapStrategy extends BaseSnapStrategy {
  private readonly showCrosshairOnDataPoints: boolean;

  constructor(showCrosshairOnDataPoints: boolean = true) {
    super(NO_BANDWIDTH);
    this.showCrosshairOnDataPoints = showCrosshairOnDataPoints;
  }

  findClosestDataPoint(context: SnapStrategyContext): DataPointSnapResult | null {
    const { cursorSvgX, chartData, xDataKey, xScale, snapDistance } = context;

    let result: DataPointSnapResult | null = null;

    for (const datum of chartData) {
      const xValue = datum[xDataKey];
      if (typeof xValue !== 'number') {
        continue;
      }

      const scaleInput = this.getScaleInputForValue(xValue, context);
      const scaledX = xScale(scaleInput as number);
      if (typeof scaledX !== 'number') {
        continue;
      }

      // For point snapping, use distance to the point
      const distance = Math.abs(scaledX - cursorSvgX);
      if (distance <= snapDistance && (!result || distance < result.distance)) {
        result = { xValue, xPosition: scaledX, distance };
      }
    }

    return result;
  }

  shouldShowCrosshair(hasDataPoint: boolean, hasNearbyReferenceLine: boolean): boolean {
    if (this.showCrosshairOnDataPoints) {
      // Show crosshair when snapped to data point or reference line
      return hasDataPoint || hasNearbyReferenceLine;
    }
    // Only show crosshair on reference lines
    return hasNearbyReferenceLine;
  }
}
